import { SubTradeRequestStates } from '@generated/graphql';
import type { SubTradeRequest } from '@interfaces/subTradeRequest';
import { CurrencyCodes } from '@onevesthq/ov-enums';
import { TableCell } from '@ovComponents/2-component/table/table';
import { AmountField } from '@ovComponents/3-pattern/amountField/amountField';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { Box, Typography, useTheme } from 'hephaestus/web';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { TESTID_PORTFOLIO_WIDGET_HOLDINGS_TABLE_ROW_CELL } from '../portfolioTestIds';
import ChangeTooltip from './changeTooltip';
import type {
  DraftSubTradeRequest,
  PortfolioSecurity,
  PortfolioSubAccount,
} from './interfaces';
import { isTradeableAllocationMember } from './tradeableAllocationMembers';
import {
  getAmountFieldInputColorByKey,
  getHoldingCurrentValueCents,
  getHoldingOriginalValueCents,
  getIsMultiCurrencySubAccount,
  getPendingAmountCentsByKey,
  getPortfolioHoldingCellSx,
  getPortfolioMemberColumnSx,
  holdingTradeKey,
  isOffModelWithoutBalance,
  isSellAllByKey,
  PORTFOLIO_ROW_HEIGHT,
} from './utils';

export const SubAccountHoldingTableCell = ({
  security,
  subAccount,
  draftTrades,
  pendingTrades,
  handleHoldingChange,
  viewPreTradePositions = false,
  isMakeHigher = false,
  readOnly = false,
  subAccountTradeRequests = [],
  index = 0,
  subIndex = 0,
  currency,
  showCustomTargetColumn = false,
  originalValueCentsOverride,
  holdingAmountDisplayMode = 'proposedEnd',
}: {
  security: PortfolioSecurity;
  subAccount: PortfolioSubAccount;
  draftTrades: Map<string, DraftSubTradeRequest>;
  pendingTrades: Map<string, DraftSubTradeRequest>;
  handleHoldingChange: (
    security: PortfolioSecurity,
    subAccount: PortfolioSubAccount,
    amount: string,
  ) => void;
  viewPreTradePositions?: boolean;
  isMakeHigher?: boolean;
  readOnly?: boolean;
  subAccountTradeRequests?: SubTradeRequest[];
  index: number;
  subIndex: number;
  currency: CurrencyCodes;
  showCustomTargetColumn?: boolean;
  /** When holdings lack quantity (e.g. audit snapshots), use this for tooltips. */
  originalValueCentsOverride?: number;
  /** When 'currentHolding', read-only cells show live holdings without applying draft trades. */
  holdingAmountDisplayMode?: 'proposedEnd' | 'currentHolding';
}) => {
  const { t } = useTranslation(['components', 'accountTypes']);
  const theme = useTheme();
  const { localizedDate } = useLocalization();

  const financialProductId = subAccount.holding?.financialProduct?.id;
  const subAccountId = subAccount.id;

  const requestedTradesInfo = useMemo(
    () =>
      subAccountTradeRequests
        .filter(
          (req) =>
            req.subAccount?.id === subAccountId &&
            req.financialProduct?.id === financialProductId,
        )
        .map((req) => {
          const splitSum = req.splits?.reduce(
            (sum, split) =>
              split.state === SubTradeRequestStates.Requested
                ? sum + (split.moneyAllocatedCents ?? 0)
                : sum,
            0,
          );

          const moneyAllocatedCents =
            (req.splits?.length ?? 0) > 0
              ? (splitSum ?? 0)
              : (req.moneyAllocatedCents ?? 0);

          return {
            type: req.type,
            isSellAll: req.isSellAll,
            moneyAllocatedCents,
            settlementDate: req.settlementDate
              ? localizedDate(req.settlementDate)
              : 'N/A',
          };
        }),
    [subAccountTradeRequests, subAccountId, financialProductId, localizedDate],
  );

  const key = holdingTradeKey(security.id, subAccount.id);

  const draftAmountCents = useMemo(
    () => getPendingAmountCentsByKey(key, [draftTrades, pendingTrades]),
    [key, draftTrades, pendingTrades],
  );

  const pendingAmountCents = useMemo(
    () => getPendingAmountCentsByKey(key, [pendingTrades]),
    [key, pendingTrades],
  );

  const isMultiCurrencySubAccount = useMemo(
    () => getIsMultiCurrencySubAccount(subAccount),
    [subAccount],
  );

  const subAccountBaseCurrency = useMemo(
    () => subAccount?.accountBaseCurrency ?? CurrencyCodes.CAD,
    [subAccount?.accountBaseCurrency],
  );

  const isNotTradeable = !isTradeableAllocationMember(subAccount);
  const isOffModelEmpty = isOffModelWithoutBalance(security, subAccount);

  const shouldDisableInput = useMemo(() => {
    if (readOnly) {
      return true;
    }

    if (isMultiCurrencySubAccount && security.originalCurrency !== currency) {
      return true;
    }

    if (!isMultiCurrencySubAccount && subAccountBaseCurrency !== currency) {
      return true;
    }

    if (
      subAccount.inactive ||
      isNotTradeable ||
      pendingAmountCents ||
      isOffModelEmpty
    ) {
      return true;
    }

    return false;
  }, [
    readOnly,
    isMultiCurrencySubAccount,
    subAccountBaseCurrency,
    currency,
    security.originalCurrency,
    security.expectedPercentage,
    security.expectedValueCents,
    subAccount.inactive,
    subAccount.isTradeable,
    subAccount.state,
    subAccount.holding?.valueCents,
    subAccount.holding?.quantity,
    pendingAmountCents,
  ]);

  const getDisabledReason = () => {
    if (isNotTradeable) return t('householdPortfolio.tradeNotTradeable');
    if (isOffModelEmpty) return t('householdPortfolio.tradeNotHeld');
    return undefined;
  };
  const disabledReason = getDisabledReason();

  const amountCents = draftAmountCents || 0;

  const isSellAll = useMemo(
    () => isSellAllByKey(key, [draftTrades, pendingTrades]),
    [key, draftTrades, pendingTrades],
  );

  const fieldColor = useMemo(
    () =>
      getAmountFieldInputColorByKey(
        key,
        amountCents,
        [draftTrades, pendingTrades],
        theme,
      ),
    [key, amountCents, draftTrades, pendingTrades, theme],
  );

  const valueCents = useMemo(
    () => originalValueCentsOverride ?? getHoldingCurrentValueCents(subAccount),
    [originalValueCentsOverride, subAccount],
  );

  const fieldAmountCents = (() => {
    if (holdingAmountDisplayMode === 'currentHolding') {
      return valueCents ?? 0;
    }

    const proposedEndCents = (valueCents ?? 0) + amountCents;
    // Full exits can land a hair under 0¢ (float reconstruction) and render as -$0.00.
    if (isSellAll || Math.abs(proposedEndCents) < 0.5) {
      return 0;
    }

    return proposedEndCents;
  })();

  const originalBeforeRequestedCents = useMemo(
    () =>
      originalValueCentsOverride ??
      getHoldingOriginalValueCents(subAccount, security),
    [originalValueCentsOverride, subAccount, security],
  );

  const displayFieldColor = readOnly
    ? theme.color.onSurfaceVariant
    : fieldColor;

  const readOnlyInputSx = useMemo(
    () =>
      readOnly
        ? {
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: displayFieldColor,
              color: displayFieldColor,
              opacity: 1,
            },
            '& .MuiInputBase-root.Mui-disabled': {
              opacity: 1,
            },
          }
        : {},
    [displayFieldColor, readOnly],
  );

  const sx = useMemo(
    () => ({
      border: `1px solid ${Math.abs(amountCents) > 0 ? theme.color.warning : 'default'}`,
      borderRadius: '4px',
      ...readOnlyInputSx,
    }),
    [amountCents, readOnlyInputSx, theme.color.warning],
  );

  const inputProps = useMemo(
    () => ({
      'aria-label': [security.ticker, subAccount.name, disabledReason]
        .filter(Boolean)
        .join(', '),
      style: {
        fontSize: '12px',
        color: displayFieldColor,
        fontVariantNumeric: 'tabular-nums',
        minWidth: '65px',
      },
    }),
    [displayFieldColor, disabledReason, security.ticker, subAccount.name],
  );

  const handleAmountChange = (value: string) => {
    const numericValue = Number.parseFloat(value);

    if (Number.isFinite(numericValue) && numericValue < 0) {
      handleHoldingChange(security, subAccount, '0');
    } else {
      handleHoldingChange(security, subAccount, value);
    }
  };

  return (
    <TableCell
      sx={{
        ...(showCustomTargetColumn
          ? getPortfolioHoldingCellSx(subAccount.name)
          : getPortfolioMemberColumnSx(subAccount.name)),
        height: isMakeHigher
          ? PORTFOLIO_ROW_HEIGHT.extended
          : PORTFOLIO_ROW_HEIGHT.standard,
      }}
      data-testid={TESTID_PORTFOLIO_WIDGET_HOLDINGS_TABLE_ROW_CELL(
        index,
        subIndex,
      )}
    >
      <Box
        sx={{
          justifyContent: 'start',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <AmountField
          useFormatAmountValueCents
          fullWidth={false}
          size='small'
          label=''
          amount={`${fieldAmountCents}`}
          disabled={shouldDisableInput}
          sx={sx}
          inputProps={inputProps}
          setAmount={handleAmountChange}
          currency={
            isMultiCurrencySubAccount ? security.originalCurrency : undefined
          }
        />

        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 16,
          }}
        >
          <ChangeTooltip
            change={amountCents}
            original={originalBeforeRequestedCents}
            isSellAll={isSellAll}
            requestedTradeInfo={requestedTradesInfo}
          />
        </Box>
      </Box>

      {viewPreTradePositions && Math.abs(amountCents) > 0 && (
        <Typography variant='bodySmall' component='p' sx={{ display: 'block' }}>
          {t('portfolioTable.original', {
            amount: formatMoneyValue(valueCents ?? 0),
          })}
        </Typography>
      )}
    </TableCell>
  );
};
